import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useId, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface MultiSelectOption {
  value: string
  label: string
  description?: string
}

export interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  label?: string
  id?: string
  disabled?: boolean
  className?: string
}

const inputBaseClasses =
  'box-border h-auto min-h-11 w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 py-2 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100'

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  label,
  id: idProp,
  disabled = false,
  className,
}: MultiSelectProps): React.ReactElement {
  const reactId = useId()
  const id = idProp ?? `multiselect-${reactId}`
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [listPosition, setListPosition] = useState<{
    top: number
    left: number
    width: number
    maxHeight: number
    strategy: 'absolute' | 'fixed'
  } | null>(null)

  const selectedSet = useMemo(() => new Set(value), [value])
  const selectedOptions = options.filter((o) => selectedSet.has(o.value))
  const displaySummary =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selected`
  const hasValue = value.length > 0

  const close = useCallback(() => {
    setOpen(false)
    setHighlightedIndex(0)
  }, [])

  const toggleOption = useCallback(
    (optValue: string) => {
      const next = selectedSet.has(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue]
      onChange(next)
    },
    [onChange, selectedSet, value]
  )

  const updateListPosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const width = Math.max(rect.width, 200)
    const dialogContent = trigger.closest<HTMLElement>('[data-dialog-content]')
    const viewportPad = 8
    const gap = 4
    const preferredMax = 240
    const minList = 120

    if (dialogContent) {
      const containerRect = dialogContent.getBoundingClientRect()
      const spaceBelow = containerRect.bottom - rect.bottom
      const spaceAbove = rect.top - containerRect.top
      let top = rect.bottom - containerRect.top + dialogContent.scrollTop + gap
      let maxHeight = Math.min(preferredMax, Math.max(minList, spaceBelow))

      if (spaceBelow < minList && spaceAbove > spaceBelow) {
        maxHeight = Math.min(preferredMax, Math.max(minList, spaceAbove - gap))
        top = rect.top - containerRect.top + dialogContent.scrollTop - maxHeight - gap
      }

      setPortalContainer(dialogContent)
      setListPosition({
        top,
        left: rect.left - containerRect.left + dialogContent.scrollLeft,
        width,
        maxHeight,
        strategy: 'absolute',
      })
      return
    }

    const spaceBelow = window.innerHeight - rect.bottom - viewportPad
    const spaceAbove = rect.top - viewportPad
    let top = rect.bottom + gap
    let maxHeight = Math.min(preferredMax, Math.max(minList, spaceBelow))

    if (spaceBelow < minList && spaceAbove > spaceBelow) {
      maxHeight = Math.min(preferredMax, Math.max(minList, spaceAbove - gap))
      top = Math.max(viewportPad, rect.top - maxHeight - gap)
    }

    setPortalContainer(document.body)
    setListPosition({
      top,
      left: rect.left,
      width,
      maxHeight,
      strategy: 'fixed',
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updateListPosition()
    const onScrollOrResize = (): void => {
      updateListPosition()
    }
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    const triggerObserver = new ResizeObserver(updateListPosition)
    if (triggerRef.current) triggerObserver.observe(triggerRef.current)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
      triggerObserver.disconnect()
    }
  }, [open, updateListPosition])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (listRef.current?.contains(target)) return
      close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      close()
      containerRef.current?.querySelector<HTMLElement>('button')?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        setHighlightedIndex(0)
      } else if (e.key !== ' ') {
        const next = Math.min(highlightedIndex + 1, options.length - 1)
        setHighlightedIndex(next)
      }
    }
    if (e.key === 'ArrowUp' && open) {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    }
  }

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, options.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const opt = options[highlightedIndex]
      if (opt) toggleOption(opt.value)
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      containerRef.current?.querySelector<HTMLElement>('button')?.focus()
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
        >
          {label}
        </label>
      )}
      <div ref={triggerRef} className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-multiselectable
          aria-label={label || undefined}
          onClick={() => {
            if (disabled) return
            setOpen((o) => !o)
            if (!open) setHighlightedIndex(0)
          }}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            inputBaseClasses,
            'flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 text-left',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 py-0.5">
            {hasValue ? (
              selectedOptions.map((opt) => (
                <span
                  key={opt.value}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/15 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary dark:border-primary/30 dark:bg-primary/20 dark:text-blue-200"
                >
                  {opt.label}
                </span>
              ))
            ) : (
              <span className="text-gray-400 dark:text-gray-500">{displaySummary}</span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {hasValue && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear selection"
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange([])
                  }
                }}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronDown
              className={cn('h-4 w-4 text-gray-400 transition-transform', open && 'rotate-180')}
            />
          </span>
        </button>
        {open &&
          listPosition &&
          portalContainer &&
          createPortal(
            <ul
              ref={listRef}
              data-multiselect-dropdown=""
              role="listbox"
              aria-activedescendant={
                options[highlightedIndex] ? `option-${options[highlightedIndex].value}` : undefined
              }
              tabIndex={-1}
              onKeyDown={handleListKeyDown}
              className="pointer-events-auto overflow-auto rounded-2xl border border-gray-200/80 bg-white/98 p-1.5 shadow-[0_20px_55px_rgba(15,23,42,0.18)] backdrop-blur dark:border-gray-700 dark:bg-gray-800/98"
              style={{
                position: listPosition.strategy,
                top: listPosition.top,
                left: listPosition.left,
                width: listPosition.width,
                maxHeight: listPosition.maxHeight,
                zIndex: 9999,
              }}
            >
              {options.map((opt, i) => {
                const isSelected = selectedSet.has(opt.value)
                return (
                  <li
                    key={opt.value}
                    id={`option-${opt.value}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={(e) => {
                      e.preventDefault()
                      toggleOption(opt.value)
                    }}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors',
                      isSelected && 'bg-primary/10 text-primary dark:bg-primary/20',
                      i === highlightedIndex && !isSelected && 'bg-gray-100 dark:bg-gray-700/80',
                      i === highlightedIndex && isSelected && 'bg-primary/15 dark:bg-primary/25'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        isSelected
                          ? 'border-primary bg-primary text-white dark:border-primary dark:bg-primary'
                          : 'border-gray-300 dark:border-gray-500'
                      )}
                      aria-hidden
                    >
                      {isSelected ? (
                        <span className="text-[10px] font-bold">✓</span>
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{opt.label}</span>
                      {opt.description && (
                        <span
                          className={cn(
                            'mt-0.5 block truncate text-xs',
                            isSelected
                              ? 'text-primary/70 dark:text-blue-200/80'
                              : 'text-gray-500 dark:text-gray-400'
                          )}
                        >
                          {opt.description}
                        </span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>,
            portalContainer
          )}
      </div>
    </div>
  )
}

MultiSelect.displayName = 'MultiSelect'
